'use client'

import { FC, useCallback, MouseEvent, KeyboardEvent } from 'react'
import cx from 'classnames'
import styles from './BlogPostCard.module.scss'
import { useBlogPostUrl } from '@/components/Blog/hooks/useBlogPostUrl'
import {
  BlogPostCardText,
  CardTextProps,
} from '@/components/Blog/BlogHome/BlogPostCardText'
import { DynamicImage } from '@/components/DynamicImage'
import { useRouter } from 'next/navigation'

export interface BlogPostCardProps extends Omit<CardTextProps, 'url'> {
  sys: {
    id?: string
    contentType: {
      sys: {
        id: string
      }
    }
  }
  layout?: 'triple' | 'double' | 'single'
  inCarousel?: boolean
  showHeroImage?: boolean
  showSubtitle?: boolean
  className?: string
  imageCritical?: boolean
}

export const BlogPostCard: FC<BlogPostCardProps> = ({
  imageCritical,
  post,
  layout = 'single',
  showHeroImage = true,
  className,
  inCarousel,
  ...cardTextProps
}) => {
  const router = useRouter()
  const postUrl = useBlogPostUrl(post)
  const handleCardClick = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      if (event.isDefaultPrevented()) {
        return
      }
      router.push(postUrl)
    },
    [postUrl, router],
  )

  return (
    <article
      data-testid="card"
      onClick={handleCardClick}
      className={cx(
        styles.card,
        styles[layout],
        {
          [styles.imageCritical]: imageCritical,
          [styles.inCarousel]: Boolean(inCarousel),
        },
        className,
      )}
      tabIndex={0}
      onKeyDown={event => event.key === 'Enter' && handleCardClick(event)}
      itemProp="blogPost"
      itemScope
      itemType="http://schema.org/BlogPosting"
    >
      <BlogPostCardText
        post={post}
        url={postUrl}
        {...cardTextProps}
        imageCritical={imageCritical}
      />
      {showHeroImage && post.fields.heroImage && (
        <div className={cx(styles.hero)} data-testid="card-image">
          <DynamicImage
            className={styles.backgroundImage}
            alt={post.fields.title}
            image={post.fields.heroImage}
            sizes="(max-width: 720px) 100vw, (max-width: 1080px) 50vw, 33vw"
            fill
            style={{ objectFit: 'cover' }}
          />
        </div>
      )}
    </article>
  )
}
